import { useMemo, useState } from "react";
import { Link2, Facebook, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { buildShareUrl } from "@/lib/referral";

// Brand SVGs for services lucide-react doesn't cover.
const WhatsappIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.05 4.91A10 10 0 0 0 12 2C6.5 2 2 6.48 2 12c0 1.76.46 3.46 1.34 4.97L2 22l5.21-1.36A10 10 0 0 0 12 22c5.52 0 10-4.48 10-10 0-2.67-1.04-5.18-2.95-7.09zM12 20.13c-1.5 0-2.96-.4-4.24-1.16l-.3-.18-3.09.81.83-3-.2-.31A8.13 8.13 0 0 1 3.87 12c0-4.49 3.65-8.13 8.13-8.13 2.17 0 4.21.85 5.75 2.38a8.07 8.07 0 0 1 2.38 5.75c0 4.49-3.65 8.13-8.13 8.13zm4.46-6.09c-.24-.12-1.45-.71-1.68-.79-.23-.08-.39-.12-.55.12s-.63.79-.78.95c-.14.16-.29.18-.53.06-.24-.12-1.03-.38-1.96-1.21-.72-.64-1.21-1.43-1.35-1.67-.14-.24-.02-.37.1-.49.11-.11.24-.29.36-.43.12-.14.16-.24.24-.41.08-.16.04-.31-.02-.43-.06-.12-.55-1.33-.76-1.82-.2-.48-.4-.41-.55-.42-.14-.01-.31-.01-.47-.01a.91.91 0 0 0-.66.31c-.23.24-.87.85-.87 2.08 0 1.23.89 2.41 1.02 2.58.12.16 1.75 2.68 4.24 3.76.59.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.45-.59 1.66-1.17.21-.58.21-1.07.14-1.17-.06-.1-.22-.16-.46-.28z" />
  </svg>
);

const MessengerIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.17.16.14.26.34.26.55l.05 1.79c.02.57.6.94 1.12.7l2-.88c.16-.07.34-.09.51-.04.91.25 1.88.39 2.92.39 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zM6.05 14.5l2.94-4.66c.47-.74 1.48-.93 2.18-.41l2.34 1.76c.21.16.5.16.71 0l3.16-2.4c.42-.32.97.18.69.62l-2.94 4.66c-.47.74-1.48.93-2.18.41l-2.34-1.76a.6.6 0 0 0-.71 0l-3.16 2.4c-.42.32-.97-.18-.69-.62z" />
  </svg>
);

const enc = encodeURIComponent;

const ShareBtn = ({ testid, onClick, ariaLabel, children }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-label={ariaLabel}
    className="group flex items-center gap-2.5 px-4 py-2.5 border border-white/12 bg-[#0E0E11] hover:border-[#F47A20]/55 hover:bg-[#F47A20]/[0.07] transition-colors rounded-sm text-white/85 hover:text-white"
  >
    {children}
  </button>
);

export const ShareJourney = ({ url, title = "", description = "" }) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = useMemo(() => buildShareUrl(url), [url]);
  const shareText = useMemo(
    () => (title ? `${title} — ROADRIG` : "Follow the ROADRIG journey"),
    [title]
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — share it with a rider you ride with.");
      setTimeout(() => setCopied(false), 2400);
    } catch {
      toast.error("Couldn't copy. Long-press the address bar to copy manually.");
    }
  };

  const openShare = (href) => () => {
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=720");
  };

  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(shareText)}`;
  const waHref = `https://wa.me/?text=${enc(`${shareText} — ${shareUrl}`)}`;
  // Messenger needs a Facebook App ID for the proper dialog; use the share-handler fallback (works on mobile + desktop with FB login).
  const msHref = `https://www.facebook.com/dialog/send?app_id=140586622674265&link=${enc(shareUrl)}&redirect_uri=${enc(shareUrl)}`;
  const mailHref = `mailto:?subject=${enc(shareText)}&body=${enc(`Thought you'd appreciate this — the real build story behind ROADRIG.\n\n${shareUrl}\n\n${description ? description + "\n\n" : ""}— Shared from roadrig.com.au`)}`;

  return (
    <section
      data-testid="share-journey"
      className="relative overflow-hidden bg-[#0A0A0C] border border-white/8 rounded-sm"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#F47A20]/30 to-transparent" />
      <div className="relative p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
        {/* Copy */}
        <div className="md:col-span-5">
          <div className="serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold mb-3">
            SHARE WITH A RIDER
          </div>
          <h3 data-testid="share-journey-title" className="h-display text-white text-2xl md:text-3xl leading-tight mb-3">
            Share the <span className="text-[#F47A20]">Journey</span>.
          </h3>
          <p className="text-white/68 text-sm md:text-[15px] leading-relaxed">
            If you know another rider who&rsquo;d enjoy following the ROADRIG journey, share this chapter with them.
          </p>
        </div>

        {/* Buttons */}
        <div className="md:col-span-7">
          <div className="flex flex-wrap gap-2.5">
            <ShareBtn testid="share-copy-link" onClick={copyLink} ariaLabel="Copy link">
              {copied ? <Check size={14} className="text-[#F47A20]" /> : <Link2 size={14} />}
              <span className="text-[12px] tracking-[0.15em] uppercase font-bold">
                {copied ? "Copied" : "Copy Link"}
              </span>
            </ShareBtn>
            <ShareBtn testid="share-facebook" onClick={openShare(fbHref)} ariaLabel="Share on Facebook">
              <Facebook size={14} />
              <span className="text-[12px] tracking-[0.15em] uppercase font-bold">Facebook</span>
            </ShareBtn>
            <ShareBtn testid="share-whatsapp" onClick={openShare(waHref)} ariaLabel="Share on WhatsApp">
              <WhatsappIcon className="w-3.5 h-3.5" />
              <span className="text-[12px] tracking-[0.15em] uppercase font-bold">WhatsApp</span>
            </ShareBtn>
            <ShareBtn testid="share-messenger" onClick={openShare(msHref)} ariaLabel="Share on Messenger">
              <MessengerIcon className="w-3.5 h-3.5" />
              <span className="text-[12px] tracking-[0.15em] uppercase font-bold">Messenger</span>
            </ShareBtn>
            <ShareBtn testid="share-email" onClick={() => { window.location.href = mailHref; }} ariaLabel="Share via email">
              <Mail size={14} />
              <span className="text-[12px] tracking-[0.15em] uppercase font-bold">Email</span>
            </ShareBtn>
          </div>

          <p className="serial text-[10px] text-white/40 mt-5 leading-relaxed">
            Built by riders. Shared between riders.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ShareJourney;
